import request from '@/utils/request'
import { ensureHttpsUrl } from '@/utils/mediaUrl'

function unwrapPage(res) {
  if (res && res.code && res.code !== 2000) {
    return Promise.reject(new Error(res.message || '请求失败'))
  }
  const page = (res && res.items) || res || {}
  return {
    records: page.Data || page.data || page.records || [],
    total: page.totalCount != null ? page.totalCount : (page.total || 0)
  }
}

function unwrapData(res) {
  if (res && res.code && res.code !== 2000) {
    return Promise.reject(new Error(res.message || '请求失败'))
  }
  return (res && res.items) != null ? res.items : res
}

function toResourceQuery(params = {}) {
  const payload = { ...params }
  if (typeof payload.searchConditions === 'string') {
    try {
      payload.searchConditions = JSON.parse(payload.searchConditions)
    } catch (e) {
      payload.searchConditions = undefined
    }
  }
  if (Array.isArray(payload.searchConditions) && !payload.searchConditions.length) {
    payload.searchConditions = undefined
  }
  return payload
}

export function searchResources(params) {
  return request({
    url: '/pc/resource/search',
    method: 'post',
    data: toResourceQuery(params)
  }).then(unwrapPage)
}

export function fetchResourceFacets(params) {
  return request({
    url: '/pc/resource/facets',
    method: 'post',
    data: toResourceQuery(params)
  }).then(unwrapData)
}

export function fetchResourceDetail(id) {
  return request({
    url: '/pc/resource/detail',
    method: 'get',
    params: { id }
  }).then(unwrapData)
}

/** 按资源 ID 批量获取列表数据，属性 enrichment 与 /pc/resource/search 一致 */
export function fetchResourcesByIds(ids) {
  const normalizedIds = (Array.isArray(ids) ? ids : [ids])
    .map((id) => Number(id))
    .filter((id) => Number.isFinite(id) && id > 0)

  if (!normalizedIds.length) {
    return Promise.resolve([])
  }

  return request({
    url: '/pc/resource/listByIds',
    method: 'get',
    params: { ids: normalizedIds.join(',') }
  }).then(unwrapData).then((data) => (Array.isArray(data) ? data : []))
}

export function mapResourceItem(item) {
  const attributes = (item.attributes || []).map(mapResourceAttribute)
  return {
    id: item.id,
    title: item.title,
    resourceType: item.resourceType,
    resourceTypeLabel: item.resourceTypeLabel || '',
    image: ensureHttpsUrl(item.coverImage),
    abstract: item.abstractText || '',
    attributes
  }
}

export function mapResourceDetail(item) {
  const attributes = (item.attributes || []).map(mapResourceAttribute)
  const abstractField = attributes.find((attr) => isAbstractLabel(attr.label))
  const metaAttributes = attributes.filter((attr) => !isAbstractLabel(attr.label))
  const mappedFiles = (item.files || []).map(mapResourceFile)
  const files = mappedFiles.filter((file) => !file.website)
  const websites = mappedFiles.filter((file) => !!file.website)

  return {
    id: item.id,
    title: item.title,
    resourceType: item.resourceType || '',
    coverImage: ensureHttpsUrl(item.coverImage),
    resourceTypeLabel: item.resourceTypeLabel || '',
    abstract: abstractField && abstractField.value ? abstractField.value : '',
    metaAttributes,
    collectionNo: String(item.id),
    fileCount: files.length,
    files,
    websites
  }
}

const ABSTRACT_LABELS = ['摘要', '描述']

function isAbstractLabel(label) {
  return ABSTRACT_LABELS.includes(label)
}

export function mapResourceAttribute(attr) {
  return {
    id: attr.adjunctWordId,
    code: attr.code || '',
    label: attr.label || '',
    value: attr.value || '',
    sortOrder: attr.sortOrder
  }
}

export function mapResourceFile(file) {
  const website = normalizeWebsiteUrl(file.website)
  const isWebsiteOnly = !!website
  // 后端若把空 website 误判为参考站，可能下发无效 previewUrl（如 https://""），需丢弃
  const rawPreview = (file.previewUrl || '').trim()
  const previewLooksLikeBogusWebsite =
    !isWebsiteOnly &&
    (/^https?:\/\/(""|''|null|undefined)?$/i.test(rawPreview) ||
      rawPreview === 'https://""' ||
      rawPreview === "https://''")
  return {
    id: file.id,
    name: file.fileName || (website ? website : '未命名文件'),
    type: isWebsiteOnly ? '参考网站' : (file.fileTypeLabel || '文件'),
    size: file.fileSizeLabel || '-',
    previewUrl: isWebsiteOnly
      ? (website || '')
      : (previewLooksLikeBogusWebsite ? '' : rawPreview),
    website
  }
}

/** 清洗 website：库中常见空串、字面量 "" / ''，避免被当成有效外链 */
function sanitizeWebsite(url) {
  if (url == null) return ''
  let trimmed = String(url).trim()
  if (
    !trimmed ||
    trimmed === '""' ||
    trimmed === "''" ||
    /^null$/i.test(trimmed) ||
    /^undefined$/i.test(trimmed)
  ) {
    return ''
  }
  if (
    (trimmed.startsWith('"') && trimmed.endsWith('"') && trimmed.length >= 2) ||
    (trimmed.startsWith("'") && trimmed.endsWith("'") && trimmed.length >= 2)
  ) {
    trimmed = trimmed.slice(1, -1).trim()
  }
  if (!trimmed || trimmed === '""' || trimmed === "''") {
    return ''
  }
  return trimmed
}

function normalizeWebsiteUrl(url) {
  const cleaned = sanitizeWebsite(url)
  if (!cleaned) return ''
  const withProtocol = /^https?:\/\//i.test(cleaned) ? cleaned : `https://${cleaned}`
  return ensureHttpsUrl(withProtocol) || withProtocol
}

export function mapResourceTypeFacets(types) {
  return mapFacetList(types)
}

export function mapFacetList(list, fallback) {
  if (list && list.length) {
    return list.map((item) => ({
      key: item.key,
      label: item.label,
      count: item.count || 0
    }))
  }
  return (fallback || []).map((item) => ({ ...item, count: 0 }))
}
